import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ArrowLeft, MapPin, Receipt, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  pending: "Pendiente de confirmación",
  payment_sent: "Cobro enviado a tu correo",
  paid: "Pagado · preparando",
  preparing: "Preparándose",
  out_for_delivery: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};
const STATUS_COLOR = {
  pending: "bg-amber-500",
  payment_sent: "bg-blue-500",
  paid: "bg-emerald-500",
  preparing: "bg-violet-500",
  out_for_delivery: "bg-indigo-500",
  delivered: "bg-emerald-700",
  cancelled: "bg-rose-500",
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

export default function CustomerOrders() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/me/orders");
      setOrders(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = setInterval(load, 20_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-[#fcfbfd]" data-testid="customer-orders-page">
      <header className="bg-[#1a0f2e] text-white">
        <div className="max-w-3xl mx-auto px-5 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={28} variant="plain" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#b182ec]">Mis pedidos</p>
              <p className="font-display font-bold leading-none">{user?.name || "Cliente"}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-white hover:bg-white/10" size="sm" data-testid="customer-logout">
            <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Salir</span>
          </Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-6">
        {loading ? (
          <p className="text-[#7248ae]">Cargando…</p>
        ) : orders.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-[#b182ec]/40">
            <Receipt className="h-10 w-10 mx-auto text-[#b182ec] mb-3" />
            <p className="text-[#7248ae] mb-4">Todavía no has hecho pedidos.</p>
            <p className="text-xs text-[#7248ae]">Abre el link del restaurante que te compartieron para empezar a pedir.</p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <Card key={o.id} className="p-5 border border-[#b182ec]/30 rounded-2xl" data-testid={`order-${o.order_number}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#7248ae]">{o.order_number}</p>
                    <h3 className="font-display text-lg font-bold text-[#1a0f2e]">{o.restaurant_name || "Restaurante"}</h3>
                    <p className="text-xs text-[#7248ae]">{new Date(o.created_at).toLocaleString("es-MX")}</p>
                  </div>
                  <Badge className={`${STATUS_COLOR[o.status] || "bg-gray-500"} text-white`}>{STATUS_LABEL[o.status] || o.status}</Badge>
                </div>
                <div className="rounded-lg bg-[#fcfbfd] border border-[#b182ec]/30 p-3 text-sm">
                  {o.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between text-[#1a0f2e]">
                      <span>{it.qty} × {it.name}</span>
                      <span className="text-[#3e166c]">{formatCurrency(it.line_total)}</span>
                    </div>
                  ))}
                  <div className="border-t border-[#b182ec]/30 mt-2 pt-2 flex justify-between font-bold text-[#1a0f2e]">
                    <span>Total</span><span>{formatCurrency(o.total)}</span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-[#7248ae] flex items-start gap-1">
                  <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />{o.delivery_address}{o.delivery_references ? ` · ${o.delivery_references}` : ""}
                </p>
                {o.restaurant_slug && (
                  <div className="mt-3">
                    <Button size="sm" variant="outline" asChild className="border-[#b182ec]/50">
                      <Link to={`/r/${o.restaurant_slug}`}><ArrowLeft className="h-3 w-3 mr-1 rotate-180" />Pedir otra vez</Link>
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
